import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Advice, List, Markdown, Modal, Text } from '@Sage/hoi-poi-ui';

import useLoginLiterals from 'lib/useLoginLiterals';

import S200ukiOnboardingLayout from './components/S200ukiOnboardingLayout';
import OnboardingViewContent from './components/OnboardingViewContent';

import styles from './styles.js';

const useStyles = createUseStyles(styles, 'S200ukiOnboarding');

const S200ukiOnboarding = ({
    bgImgSrc,
    blockerCode,
    companies = [],
    confirmModalOpen = false,
    isSubmitting = false,
    isSyncRunning = false,
    logo,
    maxSelectable = 5,
    onCancelConfirm,
    onConfirmSync,
    onConnectSageId,
    onOpenConfirm,
    onRetryBootstrap,
    onRetryCompanies,
    onRetrySync,
    onSyncProgressComplete,
    onToggleCompany,
    selectedIds = [],
    syncActiveStep = 2,
    syncErrorCode,
    syncIsSuccess = false,
    view,
}) => {
    // --- Context & hooks
    const classes = useStyles();
    const { loading, t } = useLoginLiterals();

    // --- Derived state
    const selectedCompanies = useMemo(
        () => companies.filter((c) => selectedIds.includes(c.id)),
        [companies, selectedIds],
    );

    const modalTitle = t('label_s200uki_onboarding_modal_title');

    const blockerLiterals = useMemo(() => {
        if (!blockerCode) return null;
        const titleKey = `label_s200uki_blocker_${blockerCode}_title`;
        const bodyKey = `label_s200uki_blocker_${blockerCode}_body`;
        return {
            title: t(titleKey),
            body: t(bodyKey),
        };
    }, [blockerCode, t]);

    // --- Handlers
    const handleContactUs = useCallback(() => {
        window.open(t('label_s200uki_contact_us_url'), '_blank', 'noopener,noreferrer');
    }, [t]);

    // --- Memoized props
    const viewContentProps = useMemo(
        () => ({
            blockerLiterals,
            classes,
            companies,
            isSubmitting,
            isSyncRunning,
            logo,
            maxSelectable,
            onContactUs: handleContactUs,
            onConnectSageId,
            onOpenConfirm,
            onRetryBootstrap,
            onRetryCompanies,
            onRetrySync,
            onSyncProgressComplete,
            onToggleCompany,
            selectedIds,
            syncActiveStep,
            syncErrorCode,
            syncIsSuccess,
            t,
            view,
        }),
        [
            blockerLiterals,
            classes,
            companies,
            handleContactUs,
            isSubmitting,
            isSyncRunning,
            logo,
            maxSelectable,
            onConnectSageId,
            onOpenConfirm,
            onRetryBootstrap,
            onRetryCompanies,
            onRetrySync,
            onSyncProgressComplete,
            onToggleCompany,
            selectedIds,
            syncActiveStep,
            syncErrorCode,
            syncIsSuccess,
            t,
            view,
        ],
    );

    const mainModalProps = useMemo(
        () => ({
            children: <OnboardingViewContent {...viewContentProps} />,
            isOpen: true,
            overrides: {
                content: {
                    style: {
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                    },
                },
            },
            shouldCloseOnEsc: false,
            shouldCloseOnOverlayClick: false,
            size: 'large',
            title: modalTitle,
            useAutoWidth: true,
            useContentStaticHeight: true,
            useCornerClose: false,
        }),
        [modalTitle, viewContentProps],
    );

    const confirmModalProps = useMemo(
        () => ({
            children: (
                <div className={classes.confirmModalBody}>
                    <Text>{t('label_s200uki_confirm_modal_body')}</Text>
                    <List
                        bullet="check"
                        items={selectedCompanies.map((c) => c.name)}
                        overrides={{ listItem: { type: 'body', color: 'inherit' } }}
                    />
                </div>
            ),
            cancelText: t('action_cancel'),
            confirmText: t('label_s200uki_confirm_modal_confirm'),
            isConfirmLoading: false,
            isOpen: confirmModalOpen,
            onCancel: onCancelConfirm,
            onConfirm: onConfirmSync,
            onRequestClose: onCancelConfirm,
            preComponent: (
                <Advice showIcon showCollapse={false} type="semanticInfo">
                    <Markdown
                        className={classes.confirmModalWarningMarkdown}
                        content={t('label_s200uki_confirm_modal_warning')}
                        overrides={{
                            p: { type: 'caption', color: 'inherit' },
                        }}
                    />
                </Advice>
            ),
            shouldCloseOnOverlayClick: false,
            size: 'small',
            title: t('label_s200uki_confirm_modal_title'),
            useAutoWidth: true,
        }),
        [
            classes.confirmModalBody,
            classes.confirmModalWarningMarkdown,
            confirmModalOpen,
            onCancelConfirm,
            onConfirmSync,
            selectedCompanies,
            t,
        ],
    );

    // --- Render
    if (loading) {
        return <S200ukiOnboardingLayout bgImgSrc={bgImgSrc} />;
    }

    return (
        <S200ukiOnboardingLayout bgImgSrc={bgImgSrc}>
            <Modal {...mainModalProps} />
            <Modal {...confirmModalProps} />
        </S200ukiOnboardingLayout>
    );
};

S200ukiOnboarding.propTypes = {
    bgImgSrc: PropTypes.string,
    blockerCode: PropTypes.string,
    companies: PropTypes.array,
    confirmModalOpen: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    isSyncRunning: PropTypes.bool,
    logo: PropTypes.node,
    maxSelectable: PropTypes.number,
    onCancelConfirm: PropTypes.func,
    onConfirmSync: PropTypes.func,
    onConnectSageId: PropTypes.func,
    onOpenConfirm: PropTypes.func,
    onRetryBootstrap: PropTypes.func,
    onRetryCompanies: PropTypes.func,
    onRetrySync: PropTypes.func,
    onSyncProgressComplete: PropTypes.func,
    onToggleCompany: PropTypes.func,
    syncActiveStep: PropTypes.oneOf([2, 3, 4]),
    syncErrorCode: PropTypes.string,
    syncIsSuccess: PropTypes.bool,
    selectedIds: PropTypes.arrayOf(PropTypes.string),
    view: PropTypes.string.isRequired,
};

S200ukiOnboarding.displayName = 'S200ukiOnboarding';

export { VIEWS } from './constants';
export default memo(S200ukiOnboarding);
